import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ---------------------------------------------------------------------------
// Enum constants — match product_recalls check constraints in SQL
// ---------------------------------------------------------------------------

export const RECALL_CLASSES = ['CLASS_I', 'CLASS_II', 'CLASS_III'] as const;
export type RecallClass = (typeof RECALL_CLASSES)[number];

export const RECALL_TYPES_DYNAMO = ['VOLUNTARY', 'REGULATORY', 'PRECAUTIONARY'] as const;
export type RecallTypeDynamo = (typeof RECALL_TYPES_DYNAMO)[number];

export const SOURCE_ORIGINS = ['INTERNAL', 'EXTERNAL', 'REGULATORY', 'PRECAUTIONARY'] as const;
export type SourceOrigin = (typeof SOURCE_ORIGINS)[number];

export const DYNAMO_RECALL_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'APPROVED',
  'MONITORING',
  'CLOSED',
  'CANCELLED',
] as const;
export type DynamoRecallStatus = (typeof DYNAMO_RECALL_STATUSES)[number];

// ---------------------------------------------------------------------------
// Hospital recall target (embedded array on the recall record)
// ---------------------------------------------------------------------------

export const HospitalRecallTargetSchema = z.object({
  hospital_id: uuidSchema,
  notification_id: uuidSchema.nullish(),
  notification_status: z.string(),
  recall_status: z.string(),
});
export type HospitalRecallTarget = z.infer<typeof HospitalRecallTargetSchema>;

// ---------------------------------------------------------------------------
// POST — create
// ---------------------------------------------------------------------------

export const CreateProductRecallDynamoSchema = z.object({
  recall_title: z.string().trim().min(1).max(300),
  product_id: z.array(uuidSchema),
  linked_products: z.array(z.string()).default([]),
  recall_class: z.enum(RECALL_CLASSES),
  recall_type: z.enum(RECALL_TYPES_DYNAMO),
  source_origin: z.enum(SOURCE_ORIGINS),
  recall_date: z.string().date().optional(),
  source_url: z.string().url().optional(),
  affected_brand: z.string().trim().max(200).optional(),
  affected_manufacturer: z.string().trim().max(200).optional(),
  affected_form_factor: z.string().trim().max(200).optional(),
  affected_sku: z.array(z.string()).optional(),
  affected_batch_numbers: z.array(z.string()).optional(),
  affected_regions: z.array(z.string()).optional(),
  reason: z.string().trim().optional(),
  recall_details: z.string().trim().optional(),
  status: z.enum(DYNAMO_RECALL_STATUSES).default('ACTIVE'),
  created_by: uuidSchema,
  hospitals: z.array(HospitalRecallTargetSchema).optional(),
});

export type CreateProductRecallDynamo = z.infer<typeof CreateProductRecallDynamoSchema>;

// ---------------------------------------------------------------------------
// PATCH — partial update
// ---------------------------------------------------------------------------

export const UpdateProductRecallDynamoSchema = z.object({
  source_origin: z.enum(SOURCE_ORIGINS).optional(),
  source_url: z.string().url().optional(),
  linked_products: z.array(z.string()).optional(),
  recall_date: z.string().date().optional(),
  status: z.enum(DYNAMO_RECALL_STATUSES).optional(),
  hospitals: z.array(HospitalRecallTargetSchema).optional(),
}).refine(
  (data) => Object.keys(data).length > 0,
  { message: 'At least one field must be provided for update' },
);

export type UpdateProductRecallDynamo = z.infer<typeof UpdateProductRecallDynamoSchema>;

// ---------------------------------------------------------------------------
// GET list — query / cursor pagination
// ---------------------------------------------------------------------------

export const ProductRecallDynamoListQuerySchema = z.object({
  product_id: z.array(uuidSchema).optional(),
  status: z.preprocess(
    (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
    z.array(z.enum(DYNAMO_RECALL_STATUSES)).optional(),
  ),
  source_origin: z.preprocess(
    (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
    z.array(z.enum(SOURCE_ORIGINS)).min(1).optional(),
  ),
  recall_reference: z.string().trim().optional(),
  /** GSI4: filter recalls by status AND recall_date range (ISO date e.g. 2026-04-01) */
  recall_date_from: z.string().date().optional(),
  recall_date_to: z.string().date().optional(),
  /** GSI5: filter by affected_brand (+ optional affected_manufacturer narrows sort key) */
  affected_brand: z.string().trim().optional(),
  affected_manufacturer: z.string().trim().optional(),
  /** GSI6: look up all recalls affecting a specific batch / lot number */
  batch_number: z.string().trim().optional(),
  /** Case-insensitive partial match on recall_title */
  recall_title: z.string().trim().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Base-64 encoded LastEvaluatedKey from previous page */
  cursor: z.string().optional(),
});

export type ProductRecallDynamoListQuery = z.infer<typeof ProductRecallDynamoListQuerySchema>;

// ---------------------------------------------------------------------------
// Linked product detail — enriched product info returned on getById
// ---------------------------------------------------------------------------

export const LinkedProductDetailSchema = z.object({
  product_id: uuidSchema,
  product_name: z.string().nullable(),
  sku: z.string().nullable(),
  manufacturer_name: z.string().nullable(),
  form_name: z.string().nullable(),
});

export type LinkedProductDetail = z.infer<typeof LinkedProductDetailSchema>;

// ---------------------------------------------------------------------------
// Response item
// ---------------------------------------------------------------------------

export const ProductRecallDynamoItemSchema = z.object({
  recall_id: uuidSchema,
  recall_reference: z.string(),
  recall_title: z.string(),
  product_id: z.array(uuidSchema),
  linked_products: z.array(z.string()).default([]),
  recall_class: z.string(),
  recall_type: z.string(),
  source_origin: z.string(),
  recall_date: z.string().nullable(),
  source_url: z.string().nullable(),
  affected_brand: z.string().nullable(),
  affected_manufacturer: z.string().nullable(),
  affected_form_factor: z.string().nullable(),
  affected_sku: z.array(z.string()).default([]),
  affected_batch_numbers: z.array(z.string()),
  affected_regions: z.array(z.string()),
  reason: z.string(),
  recall_details: z.string().nullable(),
  status: z.string(),
  resolution_notes: z.string().nullable(),
  hospitals: z.array(HospitalRecallTargetSchema).default([]),
  is_deleted: z.boolean(),
  deleted_at: z.string().nullable(),
  deleted_by: z.string().nullable(),
  created_by: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProductRecallDynamoItem = z.infer<typeof ProductRecallDynamoItemSchema>;

// Enriched detail item returned by GET /productRecalls/:id — linked_products are full objects
export const ProductRecallDynamoDetailItemSchema = ProductRecallDynamoItemSchema.extend({
  linked_products: z.array(LinkedProductDetailSchema).default([]),
});

export type ProductRecallDynamoDetailItem = z.infer<typeof ProductRecallDynamoDetailItemSchema>;

export const ProductRecallDynamoListResponseSchema = z.object({
  items: z.array(ProductRecallDynamoItemSchema),
  /** Present when more pages exist; pass as `cursor` query param */
  next_cursor: z.string().nullable(),
  count: z.number().int(),
});

export type ProductRecallDynamoListResponse = z.infer<typeof ProductRecallDynamoListResponseSchema>;
